import { useEffect, useRef, useState } from 'react';
import { IconChevronDown, IconChevronLeft, IconChevronRight, IconClock, IconLoader2 } from '@tabler/icons-react';
import type { CalendarControl, RespondentCalendar } from '../../lib/multimodal-contract';

/** Live date and time choices share the conversation's pending and retry boundary. */
interface CalendarBookingProps {
  calendar?: RespondentCalendar;
  disabled: boolean;
  pending: boolean;
  autoLoad?: boolean;
  onSelect: (calendar: CalendarControl) => void;
}

const focus = 'focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-volt';
const button = `${focus} cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-50`;

/** Older browsers still offer common zones alongside their detected/current zone. */
const timezones = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone')
  ?? ['America/Los_Angeles', 'America/New_York', 'America/Sao_Paulo', 'Europe/London', 'Europe/Paris',
    'Africa/Johannesburg', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland'];

/** Shift a calendar date without DST or browser timezone affecting the day. */
function shiftDay(day: string, offset: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + offset * 86_400_000).toISOString().slice(0, 10);
}

/** A compact, optional selection surface for the text conversation's live availability. */
export function CalendarBooking({ calendar, disabled, pending, autoLoad = true, onSelect }: CalendarBookingProps) {
  const loadAttempted = useRef(false);
  useEffect(() => {
    if (!autoLoad || disabled || calendar?.page || loadAttempted.current) return;
    loadAttempted.current = true;
    onSelect({ action: 'availability', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
  }, [autoLoad, disabled, calendar?.page, onSelect]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const timezone = calendar && calendar.status !== 'idle' ? calendar.timezone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneOptions = [...new Set([timezone, 'UTC', ...timezones])].sort();
  const slots = calendar?.slots ?? [];
  const dayFormat = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const timeFormat = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit' });
  const dateFormat = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const availableDays = new Set(slots.map(slot => dayFormat.format(new Date(slot.startsAt))));
  const page = calendar?.page;
  const days = page ? Array.from({ length: Math.min(7, Math.round((Date.parse(page.endDay) - Date.parse(page.startDay)) / 86_400_000) + 1) },
    (_, index) => shiftDay(page.startDay, index)) : [...availableDays];
  const activeDay = selectedDay && availableDays.has(selectedDay) ? selectedDay : [...availableDays][0];
  const weekFormat = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' });
  const dateLabel = (day: string) => new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(day));
  const daySlots = slots.filter(slot => dayFormat.format(new Date(slot.startsAt)) === activeDay);
  const selected = daySlots.find(slot => slot.id === selectedId);

  /** Browsing changes the visible week without sending a conversation answer. */
  const changeWeek = (offset: number) => {
    if (!page) return;
    setSelectedId(null);
    setSelectedDay(null);
    const requested = shiftDay(page.startDay, offset);
    onSelect({ action: 'availability', earliestDay: requested < page.firstDay ? page.firstDay : requested, timezone });
  };

  return <section aria-label="Choose a meeting time" aria-busy={pending} className="space-y-3">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
      {calendar ? <span className="inline-flex items-center gap-1.5"><IconClock size={16} aria-hidden="true" />{calendar.durationMinutes} min</span> : null}
      <div className="relative min-w-0 max-w-full">
        <select aria-label="Timezone" name="timezone" autoComplete="off" value={timezone} disabled={disabled || pending}
          onChange={event => {
            setSelectedId(null);
            setSelectedDay(null);
            onSelect({ action: 'availability', timezone: event.target.value, ...(page ? { earliestDay: page.startDay } : {}) });
          }}
          className="peer absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed">
          {timezoneOptions.map(zone => <option key={zone} value={zone}>{zone.replaceAll('_', ' ')}</option>)}
        </select>
        <span aria-hidden="true" className="pointer-events-none flex min-h-11 items-center gap-1.5 rounded-lg px-2 transition-colors peer-hover:bg-surface-hover peer-focus-visible:outline-1 peer-focus-visible:-outline-offset-1 peer-focus-visible:outline-volt peer-disabled:opacity-50">
          <span className="truncate">{timezone.replaceAll('_', ' ')}</span><IconChevronDown size={14} className="shrink-0" />
        </span>
      </div>
      {pending ? <span role="status" aria-label="Updating available times" className="ml-auto"><IconLoader2 size={16} aria-hidden="true" className="animate-spin motion-reduce:animate-none" /></span> : null}
    </div>
    <div className="space-y-3">
      {days.length ? <>
        {page ? <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-foreground" aria-live="polite">{weekFormat.formatRange(new Date(page.startDay), new Date(page.endDay))}</span>
          <div className="flex gap-1">
            <button type="button" aria-label="Previous week" disabled={disabled || page.startDay <= page.firstDay} onClick={() => changeWeek(-7)}
              className={`${button} flex size-11 items-center justify-center rounded-lg hover:bg-surface-hover`}><IconChevronLeft size={18} aria-hidden="true" /></button>
            <button type="button" aria-label="Next week" disabled={disabled || page.endDay >= page.lastDay} onClick={() => changeWeek(7)}
              className={`${button} flex size-11 items-center justify-center rounded-lg hover:bg-surface-hover`}><IconChevronRight size={18} aria-hidden="true" /></button>
          </div>
        </div> : null}
        <div role="group" aria-label="Available dates" className="grid grid-cols-7 gap-1">
          {days.map(day => <button key={day} type="button" aria-label={dateLabel(day)} aria-pressed={day === activeDay}
            disabled={disabled || !availableDays.has(day)} onClick={() => { setSelectedDay(day); setSelectedId(null); }}
            className={`${button} flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-lg py-2 text-sm ${day === activeDay ? 'bg-surface-hover text-foreground font-medium' : 'text-muted-foreground hover:bg-surface-raised'}`}>
            <span className="text-xs">{new Date(day).toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short' })}</span>
            <span className="tabular-nums">{Number(day.slice(-2))}</span>
          </button>)}
        </div>
        <div key={activeDay} role="group" aria-label="Available times" className="grid max-h-60 grid-cols-3 gap-2 overflow-y-auto p-1 -mx-1">
          {daySlots.map(slot => <button key={slot.id} type="button" disabled={disabled} aria-pressed={selected?.id === slot.id}
            aria-label={`${timeFormat.format(new Date(slot.startsAt))}, ${dateFormat.format(new Date(slot.startsAt))}`}
            onClick={() => setSelectedId(slot.id)}
            className={`${button} min-h-11 rounded-lg px-3 py-2 text-sm font-medium tabular-nums ${selected?.id === slot.id ? 'button-accent' : 'bg-surface text-foreground hover:bg-surface-hover'}`}>
            {timeFormat.format(new Date(slot.startsAt))}
          </button>)}
        </div>
        {selected ? <button type="button" disabled={disabled} onClick={() => onSelect({ action: 'book', slotId: selected.id })}
          className={`${button} w-full min-h-11 rounded-lg button-accent px-4 py-3 text-sm font-medium`}>Book time</button> : null}
      </> : null}
      {calendar?.status === 'empty' ? <p role="status" className="text-sm text-muted-foreground">No times available.</p> : null}
      {!calendar || calendar.status === 'idle' ? <div aria-hidden="true" className="grid grid-cols-3 gap-2 motion-safe:animate-pulse">
        {Array.from({ length: 6 }, (_, index) => <div key={index} className="h-11 rounded-lg bg-surface" />)}
      </div> : null}
    </div>
  </section>;
}
